import {
  PathfinderDivideIcon,
  PauseIcon,
  PlayIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { useQueryClient } from "@tanstack/react-query";
import {
  placeOnHoldApplication,
  resumeApplication
} from "shared/app/OnHoldResume/api";
import { Action } from "shared/components/ContextMenuPopover";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApplicationActions } from "shared/utils/application-actions";

import { Archive, ArrowRight, FlipBackward } from "untitledui-js-base";
import { MenuRow } from "views/applications-id/components/LayoutHeader/components/MenuRow";

export const usePopoverActions = ({
  applicationId,
  actions: appActions,
  onMoveClick,
  onRefundClick,
  onCancelClick,
  onUncancelClick,
  onSplitClick
}: {
  applicationId: string | number;
  actions: ApplicationActions;
  onMoveClick?: () => void;
  onCancelClick?: () => void;
  onUncancelClick?: () => void;
  onRefundClick?: () => void;
  onSplitClick?: () => void;
}) => {
  const { unit } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();

  const actions = appActions;

  const ret: Action[] = [];

  // Move to another unit
  const { move } = actions;
  if (!move.hidden)
    ret.push({
      title: (
        <MenuRow
          text={`Move to another ${unit}`}
          Icon={ArrowRight}
          isDisabled={move.disabled}
        />
      ),
      tooltip: move.tooltip,
      disabled: move.disabled,
      onClick: () => onMoveClick?.()
    });

  // Split application
  const { split } = actions;
  if (!split.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Split application"
          HugeIcon={PathfinderDivideIcon}
          isDisabled={split.disabled}
        />
      ),
      disabled: split.disabled,
      tooltip: split.tooltip,
      onClick: () => onSplitClick?.()
    });

  // Resume / Place on hold
  const { resume, placeOnHold } = actions;

  if (!resume.hidden)
    ret.push({
      title: <MenuRow text="Resume" HugeIcon={PlayIcon} />,
      onClick: () => {
        resumeApplication({
          applicationId,
          onSuccess: () => {
            queryClient.refetchQueries({
              queryKey: ["APPLICATION", applicationId]
            });
          }
        });
      }
    });
  else if (!placeOnHold.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Place on hold"
          HugeIcon={PauseIcon}
          isDisabled={placeOnHold.disabled}
        />
      ),
      disabled: placeOnHold.disabled,
      tooltip: placeOnHold.tooltip,
      onClick: () => {
        placeOnHoldApplication({
          applicationId,
          onSuccess: () => {
            queryClient.refetchQueries({
              queryKey: ["APPLICATION", applicationId]
            });
          }
        });
      }
    });

  // Uncancel / Cancel
  const { uncancel, cancel } = actions;

  if (!uncancel.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Uncancel application"
          Icon={Archive}
          isDisabled={uncancel.disabled}
        />
      ),
      disabled: uncancel.disabled,
      tooltip: uncancel.tooltip,
      onClick: () => onUncancelClick?.()
    });
  else if (!cancel.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Cancel application"
          Icon={Archive}
          isDisabled={cancel.disabled}
        />
      ),
      tooltip: cancel.tooltip,
      disabled: cancel.disabled,
      onClick: () => onCancelClick?.()
    });

  // Refund application
  const { refund } = actions;
  if (!refund.hidden)
    ret.push({
      title: (
        <MenuRow
          text="Refund application"
          Icon={FlipBackward}
          isDisabled={refund.disabled}
        />
      ),
      disabled: refund.disabled,
      tooltip: refund.tooltip,
      onClick: () => onRefundClick?.()
    });

  return ret;
};
